import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/supabase.config';
import { useAuth } from '@/context/authContext';
import { isInternalEmail } from '@/lib/loginIdentifier';

export interface MyAssignment {
  id: string;
  scope: 'NATIONAL' | 'DISTRICT' | 'REGION';
  role: { name: string } | null;
  district: { name: string } | null;
  region: { name: string } | null;
}

export interface MyAccount {
  id: string;
  full_name: string | null;
  username: string | null;
  email: string | null;
  contact_email: string | null;
  must_change_password: boolean;
  is_active: boolean;
  assignments: MyAssignment[];
}

export const myAccountKey = (userId: string | undefined) => ['my_account', userId] as const;

/** The email to show: the contact email, or the login email unless it is the internal address */
export function displayEmail(account: Pick<MyAccount, 'contact_email' | 'email'> | null | undefined): string | null {
  if (!account) return null;
  if (account.contact_email) return account.contact_email;
  return account.email && !isInternalEmail(account.email) ? account.email : null;
}

/** Profile, flags and role assignments of the signed-in user (own rows only) */
export function useMyAccount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: myAccountKey(user?.id),
    enabled: !!user?.id,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MyAccount> => {
      const [profile, roles] = await Promise.all([
        supabase
          .from('users')
          .select('id, full_name, username, email, contact_email, must_change_password, is_active')
          .eq('id', user!.id)
          .single(),
        supabase
          .from('user_roles')
          .select('id, scope, role:roles(name), district:districts(name), region:regions(name)')
          .eq('user_id', user!.id)
          .order('granted_at'),
      ]);
      if (profile.error) throw profile.error;
      if (roles.error) throw roles.error;
      return { ...profile.data, assignments: (roles.data ?? []) as unknown as MyAssignment[] };
    },
  });
}
